// Extracted from web/components/WorkbenchDialogs.tsx; behavior and declaration bodies preserved.
import { useEffect, useMemo, useState } from "react";

import { ArrowDownToLine, Check, CheckCircle2 } from "lucide-react";

import type { ModelDocument } from "../../../shared/contracts";
import { compareModels } from "../../../engineering";
import { api } from "../../api";
import { Button, Field, Modal, Notice, downloadText, errorText } from "../../components/Primitives";

export function HistoryDialog({
  document,
  baseline,
  pendingCount,
  canPublish,
  onClose,
  onPublished,
}: {
  document: ModelDocument;
  baseline: ModelDocument;
  pendingCount: number;
  canPublish: boolean;
  onClose: () => void;
  onPublished: (p: any) => void;
}) {
  const [history, setHistory] = useState<any[]>([]);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("history");
  const [publication, setPublication] = useState<any>();
  const changes = useMemo(
    () => compareModels(baseline, document),
    [baseline, document],
  );
  useEffect(() => {
    api(`/models/${document.model.modelId}/history`)
      .then((r: any) => setHistory(Array.isArray(r) ? r : r.commits || []))
      .catch((e: any) => setError(errorText(e)));
  }, [document.model.modelId]);
  return (
    <Modal
      title="History & publication"
      subtitle="Saved revisions, local changes, and approved outputs are separate states."
      onClose={onClose}
      wide
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div className="tab-bar">
        {[
          ["history", "Revision history"],
          ["compare", `Draft changes (${changes.length})`],
          ["publish", "Publish a revision"],
        ].map(([id, label]) => (
          <button
            key={id}
            className={tab === id ? "active" : ""}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {tab === "history" && (
        <div className="history-list">
          {!history.length ? (
            <p className="subtle">No saved history available.</p>
          ) : (
            history.map((h, i) => (
              <div className="history-item" key={h.id || h.commitId || i}>
                <span className="history-marker" />
                <div>
                  <strong>{h.summary || "Model revision"}</strong>
                  <span>
                    Revision {h.revision} ·{" "}
                    {typeof h.actor === "string"
                      ? h.actor
                      : h.actor?.name || "Authorized actor"}
                  </span>
                  <small>
                    {new Date(h.createdAt || h.acceptedAt).toLocaleString()}
                  </small>
                  <code>{h.id || h.commitId}</code>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      {tab === "compare" && (
        <>
          <Notice>
            This compares the current local draft with its saved baseline.
            Historical revision comparison is not available through this build’s
            public API.
          </Notice>
          {changes.length === 0 ? (
            <div className="validation-clean">
              <Check size={16} /> Draft and saved baseline are identical.
            </div>
          ) : (
            changes.map((c, i) => (
              <div className={`change-row ${c.kind}`} key={i}>
                <span className="tag">{c.kind}</span>
                <strong>{c.name}</strong>
                <p>{c.details}</p>
              </div>
            ))
          )}
        </>
      )}
      {tab === "publish" && (
        <>
          <Notice>
            Publication captures an immutable saved revision for permitted
            consumers. It does not deploy a database.
          </Notice>
          {pendingCount > 0 && (
            <Notice kind="warning">
              Save your {pendingCount} pending operations before publication.
            </Notice>
          )}
          <Field label="Publication name">
            <input
              placeholder="e.g. Architecture review · October"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Button
            variant="primary"
            disabled={pendingCount > 0 || !name.trim() || !canPublish}
            title={
              !canPublish ? "Requires model.publish permission" : undefined
            }
            busy={busy}
            onClick={async () => {
              setBusy(true);
              setError("");
              try {
                const p = await api(
                  `/models/${document.model.modelId}/publications`,
                  { method: "POST", body: JSON.stringify({ name }) },
                );
                setPublication(p);
                onPublished(p);
              } catch (e) {
                setError(errorText(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            Publish saved revision
          </Button>
          {publication && (
            <div className="publication-result">
              <CheckCircle2 size={21} />
              <strong>Publication created</strong>
              <code>{publication.publicationId || publication.id}</code>
              <Button
                icon={ArrowDownToLine}
                onClick={() =>
                  downloadText(
                    `${name.replace(/[^a-z0-9_-]/gi, "_")}.publication.json`,
                    JSON.stringify(publication, null, 2),
                    "application/json",
                  )
                }
              >
                Download publication receipt
              </Button>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
